class DashButtons extends HTMLElement {
  constructor() {
    super();
    this.faqGroups = [];
    this.isScrollingFromClick = false;
    this.scrollTimeout = null;
    this.mobileMql = window.matchMedia('(max-width: 1023px)');
  }

  connectedCallback() {
    this.container = document.querySelector('.dash-buttons-container') || this.parentElement;
    this.faqGroups = document.querySelectorAll('.faq-group');
    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    window.removeEventListener('scroll', this.handleScroll);
  }

  handleClick(event) {
    event.preventDefault();
    const dashButton = event.target;
    if (dashButton.tagName !== 'A' || !dashButton.dataset.targetId) return;

    this.isScrollingFromClick = true;

    this.setActiveButton(dashButton);

    const targetElement = document.getElementById(dashButton.dataset.targetId);
    if (targetElement) {
      // Move focus to the newly relevant content so the next Tab continues
      // from a predictable location instead of the activated anchor.
      targetElement.setAttribute('tabindex', '-1');
      targetElement.focus({ preventScroll: true });

      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      setTimeout(() => {
        this.isScrollingFromClick = false;
      }, 1000);
    }
  }

  handleScroll(event) {
    const scrollTarget = event?.target;
    const isWindowScroll = scrollTarget === document;
    const scrollPosition = isWindowScroll
      ? window.scrollY || document.documentElement.scrollTop
      : (scrollTarget?.scrollTop ?? (window.scrollY || document.documentElement.scrollTop));

    this.handleFixedPosition(scrollPosition);

    if (!this.isScrollingFromClick) {
      this.handleScrollActivation(scrollPosition, isWindowScroll ? null : scrollTarget);
    }
  }

  handleFixedPosition(scrollPosition) {
    const dashButtonsPosition = this.offsetTop;

    if (scrollPosition > dashButtonsPosition) {
      this.container.classList.add('dash-button-fixed');
    } else {
      this.container.classList.remove('dash-button-fixed');
    }
  }

  handleScrollActivation(scrollPosition, scrollTarget) {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      this.updateActiveButtonOnScroll(scrollPosition, scrollTarget);
    }, 50);
  }

  updateActiveButtonOnScroll(scrollPosition, scrollTarget) {
    const offset = 250;

    if (scrollPosition === 0) {
      this.removeAllActiveButtons();
      return;
    }

    const viewportHeight = scrollTarget ? scrollTarget.clientHeight : window.innerHeight;
    const totalHeight = scrollTarget ? scrollTarget.scrollHeight : document.documentElement.scrollHeight;
    const scrolledToBottom = scrollPosition + viewportHeight >= totalHeight - 100;

    if (scrolledToBottom) {
      this.removeAllActiveButtons();
      return;
    }

    let activeSection = null;
    let closestDistance = Infinity;

    this.faqGroups.forEach((faqGroup) => {
      const titleElement = faqGroup.querySelector('.faq-group-title');
      if (!titleElement || !titleElement.id) return;

      const rect = titleElement.getBoundingClientRect();
      const elementTop = rect.top + scrollPosition;
      const distance = Math.abs(elementTop - (scrollPosition + offset));

      if (distance < closestDistance && rect.top <= offset) {
        closestDistance = distance;
        activeSection = titleElement.id;
      }
    });

    if (activeSection) {
      const activeButton = this.querySelector('a[data-target-id="' + activeSection + '"]');
      if (activeButton) {
        this.setActiveButton(activeButton);
      }
    }
  }

  setActiveButton(activeButton) {
    this.removeAllActiveButtons();
    activeButton.classList.add('dash-button-active', 'active');
    this.scrollActiveChipIntoView(activeButton);
  }

  scrollActiveChipIntoView(activeButton) {
    if (!activeButton) return;
    if (!this.mobileMql || !this.mobileMql.matches) return;

    try {
      activeButton.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    } catch (e) {
      // Ignore scroll errors
    }
  }

  removeAllActiveButtons() {
    const allDashButtons = this.querySelectorAll('a');
    allDashButtons.forEach((btn) => btn.classList.remove('dash-button-active', 'active'));
  }

  render() {
    this.innerHTML = '';

    this.faqGroups.forEach((faqGroup) => {
      const faqGroupTitle = faqGroup.querySelector('.faq-group-title');
      if (!faqGroupTitle || !faqGroupTitle.id) return;

      const dashButton = document.createElement('a');
      dashButton.textContent = faqGroupTitle.textContent.trim();
      dashButton.href = '#' + faqGroupTitle.id;
      dashButton.dataset.targetId = faqGroupTitle.id;
      dashButton.setAttribute('role', 'button');
      dashButton.classList.add('button-outline', 'small');

      this.appendChild(dashButton);
    });

    this.removeEventListener('click', this.handleClick);
    window.removeEventListener('scroll', this.handleScroll);

    this.addEventListener('click', this.handleClick.bind(this));
    window.addEventListener('scroll', this.handleScroll.bind(this));

    const pageWrapper = document.querySelector('.page-wrapper');
    if (pageWrapper) {
      pageWrapper.removeEventListener('scroll', this.handleScroll);
      pageWrapper.addEventListener('scroll', this.handleScroll.bind(this));
    }

    const initiallyActive = this.querySelector('a.dash-button-active') || this.querySelector('a');
    if (initiallyActive) {
      this.scrollActiveChipIntoView(initiallyActive);
    }
  }
}

customElements.define('dash-buttons', DashButtons);
